import { Component, ErrorInfo, ReactNode } from "react";

import { Button } from "@/components/ui/buttons/button";

interface Props {
	children: ReactNode;
	fallback?: ReactNode;
}

interface State {
	error: Error | null;
	errorInfo: ErrorInfo | null;
	hasError: boolean;
}

/**
 * Error Boundary component to catch React errors and prevent app crashes
 *
 * This component catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of crashing the entire app.
 */
export class ErrorBoundary extends Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {
			error: null,
			errorInfo: null,
			hasError: false,
		};
	}

	static getDerivedStateFromError(error: Error): State {
		// Update state so the next render will show the fallback UI
		return {
			error,
			errorInfo: null,
			hasError: true,
		};
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		// Log the error to console for debugging
		console.error("ErrorBoundary caught an error:", error, errorInfo);

		// Update state with error info
		this.setState({
			error,
			errorInfo,
		});

		// You could also log the error to an error reporting service here
		// Example: logErrorToService(error, errorInfo);
	}

	handleGoHome = () => {
		window.location.href = "/";
	};

	handleReload = () => {
		window.location.reload();
	};

	handleReset = () => {
		this.setState({
			error: null,
			errorInfo: null,
			hasError: false,
		});
	};

	render() {
		if (this.state.hasError) {
			// If a custom fallback is provided, use it
			if (this.props.fallback) {
				return this.props.fallback;
			}

			// Default error UI
			return (
				<section className="flex min-h-screen items-start bg-primary py-16 md:items-center md:py-24">
					<div className="mx-auto max-w-container grow px-4 md:px-8">
						<div className="flex w-full max-w-3xl flex-col gap-8 md:gap-12">
							<div className="flex flex-col gap-4 md:gap-6">
								<div className="flex flex-col gap-3">
									<span className="text-md font-semibold text-brand-secondary">
										Something went wrong
									</span>
									<h1 className="text-display-md font-semibold text-primary md:text-display-lg lg:text-display-xl">
										Oops! An error occurred
									</h1>
								</div>
								<p className="text-lg text-tertiary md:text-xl">
									We're sorry, but something unexpected happened. Please try
									refreshing the page or contact support if the problem
									persists.
								</p>

								{/* Show error details in development */}
								{import.meta.env.DEV && this.state.error && (
									<div className="bg-error/10 mt-4 rounded-lg border border-error/20 p-4">
										<p className="text-error mb-2 text-sm font-semibold">
											Error Details (Development Only):
										</p>
										<pre className="max-h-48 overflow-auto text-xs text-tertiary">
											{this.state.error.toString()}
											{this.state.errorInfo?.componentStack && (
												<div className="mt-2 border-t border-error/20 pt-2">
													{this.state.errorInfo.componentStack}
												</div>
											)}
										</pre>
									</div>
								)}
							</div>

							<div className="flex flex-col-reverse gap-3 sm:flex-row">
								<Button color="secondary" onClick={this.handleReset} size="xl">
									Try again
								</Button>
								<Button onClick={this.handleGoHome} size="xl">
									Go to home
								</Button>
								<Button onClick={this.handleReload} size="xl">
									Reload page
								</Button>
							</div>
						</div>
					</div>
				</section>
			);
		}

		return this.props.children;
	}
}
